import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Col, Divider, Form, Popconfirm, Row } from 'antd';

import {
  getMilageByStopsInProgress,
  getMilageByStopsInSuccess,
} from '../../../../../../../redux/shipment/ShipmentActions';
import { valueToString } from '../../../../../../../utilz';
import { routeFileSelector } from '../../../../../../../utilz/i18n';
import { alignStopsByStopNo, parseStopsForMilageByStops } from '../../../../../../../utilz/shipmentDataManupulation';
import Button from '../../../../../../components/Buttons/Button';
import FormLabel from '../../../../../../components/Labels/FormLabel';
import SecondaryLabel from '../../../../../../components/Labels/SecondaryLabel';
import TMSTransportationDetailsCard from './TMSTransportationDetailsCard';

const TMSTransportationDetails = () => {
  const dispatch = useDispatch();
  const { getMilageByStops } = useSelector((state) => state.shipment);
  const [form] = Form.useForm();
  const formStops = Form.useWatch('stops', form);
  const [stopsData, setStopsData] = useState([]);
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);

  const handleDataChange = (data, index) => {
    const stopsLocal = form.getFieldValue('stops');
    stopsLocal[index] = { ...stopsLocal[index], ...data };
    form.setFieldValue('stops', stopsLocal);
  };

  const handleSubmit = async (data, index) => {
    try {
      await form.validateFields();
      let stopsLocal = form.getFieldValue('stops');
      stopsLocal[index] = { ...stopsLocal[index], ...data };

      stopsLocal = alignStopsByStopNo(stopsLocal, index, 'stopSequence');
      form.setFieldValue('stops', stopsLocal);
      setStopsData(stopsLocal);
    } catch (error) {
      console.log(error);
    }
  };

  const isEditing = formStops?.map(({ editMode }) => Boolean(editMode))?.includes(true);

  const selectedStopsIndex = formStops
    ?.map(({ selected }, index) => (selected ? index : null))
    ?.filter((val) => val !== null);

  const handleRemove = (index) => {
    let stopsLocal = form.getFieldValue('stops');
    if (index >= 0) {
      stopsLocal.splice(index, 1);
    }
    stopsLocal = alignStopsByStopNo(stopsLocal, index, 'stopSequence');
    form.setFieldValue('stops', stopsLocal);
    setStopsData(stopsLocal);
  };

  const handleMilage = () => {
    const stopsLocal = form.getFieldValue('stops');
    const payload = {
      Stops: parseStopsForMilageByStops(stopsLocal),
    };
    dispatch(getMilageByStopsInProgress(payload));
  };

  useEffect(() => {
    return () => {
      dispatch(getMilageByStopsInSuccess({ totalMilageInKm: 0, totalMilageInMiles: 0 }));
    };
  }, [dispatch]);

  return (
    <Form
      name="stopsForm"
      form={form}
      initialValues={{
        stops: [
          {
            countryCode: 'US',
            editMode: true,
            gridOperation: 1,
            isNew: true,
            stopSequence: 1,
            stopTypeID: '1',
          },
        ],
      }}
    >
      <Form.List name="stops">
        {(stops, { add, remove }) => (
          <>
            <Row justify="space-between" align="middle">
              <Col>
                <div>
                  <Row gutter={15}>
                    <Col>
                      <FormLabel>{t('translation:default.fields.pickUp')}</FormLabel>
                      <br />
                      <SecondaryLabel className="f-14 fw-600 text-dark">00</SecondaryLabel>
                    </Col>
                    <Col>
                      <FormLabel>{t('translation:getQuote.transportationDetails.delivery')}</FormLabel>
                      <br />
                      <SecondaryLabel className="f-14 fw-600 text-dark">00</SecondaryLabel>
                    </Col>
                    <Col>
                      <FormLabel>{t('translation:getQuote.transportationDetails.totalDistanceMiles')}</FormLabel>
                      <br />
                      <SecondaryLabel className="f-14 fw-600 text-dark">
                        <NumericFormat
                          thousandSeparator
                          thousandsGroupStyle="thousand"
                          value={String(getMilageByStops?.totalMilageInMiles)}
                          displayType="text"
                          valueIsNumericString
                          decimalScale={2}
                        />
                      </SecondaryLabel>
                    </Col>
                    <Col>
                      <FormLabel>{t('translation:getQuote.transportationDetails.totalDistanceKm')}</FormLabel>
                      <br />
                      <SecondaryLabel className="f-14 fw-600 text-dark">
                        <NumericFormat
                          thousandSeparator
                          thousandsGroupStyle="thousand"
                          value={String(getMilageByStops?.totalMilageInKm)}
                          displayType="text"
                          valueIsNumericString
                          decimalScale={2}
                        />
                      </SecondaryLabel>
                    </Col>
                  </Row>
                </div>
              </Col>
              <Col>
                <Row gutter={15}>
                  <Col>
                    <Button
                      onClick={handleMilage}
                      disabled={formStops?.length < 2}
                      loading={getMilageByStops?.progressing}
                    >
                      {t('translation:default.fields.milageCalculator')}
                    </Button>
                  </Col>
                  <Col>
                    <Button
                      onClick={() => {
                        add({
                          countryCode: 'US',
                          editMode: true,
                          gridOperation: 1,
                          isNew: true,
                          stopSequence: valueToString(stops.length + 1),
                        });
                      }}
                      disabled={isEditing}
                    >
                      {t('translation:getQuote.transportationDetails.buttons.addStop')}
                    </Button>
                  </Col>
                  <Col>
                    <Popconfirm
                      placement="topRight"
                      title={t('translation:default.fields.modal.label')}
                      onConfirm={() => {
                        remove(selectedStopsIndex);
                        handleRemove();
                      }}
                      okText={t('translation:default.fields.modal.okText')}
                      cancelText={t('translation:default.options.no')}
                      overlayClassName="delete-popup-container"
                    >
                      <Button variant="danger" type="default" disabled={selectedStopsIndex?.length === 0}>
                        {t('translation:getQuote.transportationDetails.buttons.deleteStop')}
                      </Button>
                    </Popconfirm>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Divider className="border-top-border my-15" style={{ marginLeft: '-10%', width: '120%' }} />
            <div>
              {stops.map(({ name }, index) => (
                <TMSTransportationDetailsCard
                  key={name}
                  name={name}
                  index={index}
                  remove={handleRemove}
                  stopDetails={formStops?.[name]}
                  handleDataChange={handleDataChange}
                  handleSubmit={handleSubmit}
                  defaultValues={stopsData?.[name]}
                  isLast={stops.length === index + 1}
                  form={form}
                  totalStops={stops.length}
                />
              ))}
            </div>
          </>
        )}
      </Form.List>
    </Form>
  );
};

export default TMSTransportationDetails;
